/**
 * Logistic-service base-URL override ("point the logistic API at localhost").
 *
 * The app talks to a single gateway (`REACT_APP_API_URL`), but only *some* of
 * those paths are served by `nest-logistic-service` — the rest belong to
 * core/fleet. When the toggle is on, only the logistic paths are re-pointed at
 * a locally-running logistic service; everything else keeps hitting the normal
 * gateway, so the app stays usable (auth, providers, hubs, riders, …).
 *
 * Default is **off**. Two ways to flip it:
 *   1. Build-time — `REACT_APP_USE_LOCAL_LOGISTIC_API=true` in `.env`.
 *   2. Runtime — `window.dashLogisticApi.useLocal(true)` in the browser
 *      console, then reload. Persisted in localStorage; wins over the env var
 *      so you can flip it without restarting CRA. `useLocal(null)` clears the
 *      override and falls back to the env default.
 *
 * The path list is derived from `docs/API-INDEX.md` (routes of
 * `nest-logistic-service`), narrowed to the prefixes this app actually calls.
 */

import { storage } from '@/utils/storage';

/** Where the local logistic service listens. */
export const LOGISTIC_LOCAL_BASE_URL =
  process.env.REACT_APP_LOGISTIC_LOCAL_API_URL || 'http://localhost:8087';

/** localStorage key holding the runtime override (`'true'` | `'false'`). */
const OVERRIDE_KEY = 'useLocalLogisticApi';

/** Build-time default — anything other than the literal `true` means off. */
const ENV_DEFAULT = process.env.REACT_APP_USE_LOCAL_LOGISTIC_API === 'true';

/**
 * Path prefixes served by `nest-logistic-service`.
 *
 * Deliberately prefix-matched on the *leading* segment pair so sibling core
 * routes never get caught: `/v1/reasons` is logistic while `/mgmt/v1/reasons`
 * is core, and `/v1/hubs/:id/zones` is logistic while `/v1/pitstop` (the hub
 * master) is core.
 */
const LOGISTIC_PATH_PREFIXES = [
  '/v1/shipments',
  // In-flight local work — dynamic export config. Served by
  // nest-logistic-service; kept local while everything else (providers, drivers,
  // pitstop master) stays on the staging gateway.
  '/v1/export-templates',
  '/v1/client-export-templates',
  '/v1/packages',
  '/v1/inbound',
  '/v1/dispatch',
  '/v1/batches',
  '/v1/zones',
  '/v1/hubs/',
  // Pitstop dispatch schedules live under /v1/dispatch/pitstops/* (covered by
  // '/v1/dispatch' above) — core owns /v1/pitstop* at the LB by string prefix,
  // so a plural prefix here could never be routed to logistic.
  '/v1/resolve',
  '/v1/addresses',
  '/v1/invoices',
  '/v1/delivery-invoices',
  '/v1/dashboard',
  '/v1/scheduled-instant',
  '/v1/reasons',
  '/v1/stop-workflows',
  '/v1/retur',
  '/v1/outlet-exception',
  // Covers the ledger AND its proof uploads (`/v1/cod-orders/uploads/*`) —
  // one prefix, because the upload endpoint lives under the resource it serves.
  '/v1/cod-orders',
  // UJP (uang jalan) — requests, estimate, client configs and the vehicle /
  // energy-price / subcon-vendor masters all live under it.
  '/v1/ujp',
  // Route Planner (CR-2) — reusable route templates in the route module.
  '/v1/route-plans',
  // Route stops — the ops workflow-evidence PATCH
  // (`/v1/routes/:r/stops/:s/milestones/:k/ops-evidence`) of the 4W
  // Mark-completed modal. Trailing slash keeps it off `/v1/route-plans`.
  '/v1/routes/',
  // UJP tariff & margin (CR-4) — client tariff configs and their rings, served
  // by the tariff module of nest-logistic-service at the top-level /v1/tariffs.
  '/v1/tariffs',
  // WMS (warehouse module) — storages, SKUs, stock, outbound orders, goods
  // issues, purchase orders, inbound requests, goods receipts.
  '/v1/warehouses',
  '/internal/v1/',
] as const;

/** True when `url` is served by the logistic service. */
export const isLogisticPath = (url?: string): boolean => {
  if (!url) return false;
  // Absolute URLs are already fully addressed — never rewrite those.
  if (/^https?:\/\//i.test(url)) return false;
  const path = url.startsWith('/') ? url : `/${url}`;
  return LOGISTIC_PATH_PREFIXES.some((prefix) => path.startsWith(prefix));
};

/** Whether logistic calls should go to `LOGISTIC_LOCAL_BASE_URL`. */
export const isLogisticLocalEnabled = (): boolean => {
  const override = storage.getItem(OVERRIDE_KEY);
  if (override === 'true') return true;
  if (override === 'false') return false;
  return ENV_DEFAULT;
};

/**
 * Set (or clear) the runtime override. Pass `null` to fall back to the env
 * default. Takes effect on the next request — reload to re-fetch open screens.
 */
export const setLogisticLocalEnabled = (enabled: boolean | null): void => {
  if (enabled === null) storage.removeItem(OVERRIDE_KEY);
  else storage.setItem(OVERRIDE_KEY, String(enabled));
};

// Console handle so the toggle is reachable without a UI surface (and without
// shipping a dev switch into the production shell).
if (typeof window !== 'undefined') {
  (window as any).dashLogisticApi = {
    useLocal: (enabled: boolean | null = true) => {
      setLogisticLocalEnabled(enabled);
      // eslint-disable-next-line no-console
      console.info(
        `[logistic-api] local override = ${
          enabled === null ? 'cleared (env default)' : enabled
        } → ${isLogisticLocalEnabled() ? LOGISTIC_LOCAL_BASE_URL : 'gateway'}. Reload to apply to open screens.`
      );
    },
    status: () => ({
      enabled: isLogisticLocalEnabled(),
      localBaseUrl: LOGISTIC_LOCAL_BASE_URL,
      envDefault: ENV_DEFAULT,
      paths: [...LOGISTIC_PATH_PREFIXES],
    }),
  };
}
